import "@hotwired/turbo";
import { Application } from "@hotwired/stimulus";
import { debugLog, debugError } from "./utils";

// Steps
import CheckoutFormController from "./controllers/steps/CheckoutForm/CheckoutFormController";
import OtpController from "./controllers/steps/OTP/OtpController";
import PasskeyController from "./controllers/steps/Passkey/PasskeyController";
import PaymentOptionsController from "./controllers/steps/PaymentOptions/PaymentOptionsController";
import ConnectWalletOnlyController from "./controllers/steps/ConnectWalletOnly/ConnectWalletOnlyController";
import NotEnoughBalanceController from "./controllers/steps/NotEnoughBalance/NotEnoughBalanceController";
import CoinsubPaymentOptionsController from "./controllers/steps/CoinsubPaymentOptions/CoinsubPaymentOptionsController";
import ThirdPartyPaymentOptionsController from "./controllers/steps/ThirdPartyPaymentOptions/ThirdPartyPaymentOptionsController";
import AuthorisationDFNSWithPermitSupportController from "./controllers/steps/Authorisation/AuthorisationDFNSWithPermitSupportController";
import AuthorisationDFNSWithoutPermitSupportController from "./controllers/steps/Authorisation/AuthorisationDFNSWithoutPermitSupportController";
import Authorisation3rdPartyWithPermitSupportController from "./controllers/steps/Authorisation/Authorisation3rdPartyWithPermitSupportController";
import Authorisation3rdPartyWithoutPermitSupportController from "./controllers/steps/Authorisation/Authorisation3rdPartyWithoutPermitSupportController";
import ReviewDFNSController from "./controllers/steps/Review/ReviewDFNSController";
import Review3rdPartyWalletController from "./controllers/steps/Review/Review3rdPartyWalletController";
import FastTrackPaymentController from "./controllers/steps/FastTrack/FastTrackPaymentController";
import FastTrackOtpPermitController from "./controllers/steps/FastTrack/FastTrackOtpPermitController";
import FastTrackOtpApproveController from "./controllers/steps/FastTrack/FastTrackOtpApproveController";
import FastTrackOtpPayController from "./controllers/steps/FastTrack/FastTrackOtpPayController";
import BlockchainProgressController from "./controllers/steps/BlockchainProgress/BlockchainProgressController";
import DirectDepositController from "./controllers/steps/DirectDeposit/DirectDepositController";
import BottomSheetController from "./controllers/BottomSheetController";
import DirectDepositResultController from "./controllers/steps/DirectDepositResult/DirectDepositResultController";
import AddFundsWalletController from "./controllers/steps/AddFundsWallet/AddFundsWalletController";
import OrderSummaryController from "./controllers/steps/OrderSummary/OrderSummaryController";

// Utility Controllers
import CopyController from "./controllers/utils/CopyController";
import LoadingScreenController from "./controllers/utils/LoadingScreenController";
import AnalyticsController from "./controllers/utility/AnalyticsController";

// Other
import CheckoutController from "./controllers/CheckoutController";
import ModalContainerController from "./controllers/ModalContainerController";
import SocketController from "./controllers/SocketController";
import ErrorSurfaceController from "./controllers/ErrorSurfaceController";
import ToastController from "./controllers/ToastController";

import { storage } from "./storageconstants";
import {
  guardCompletedSession,
  isAlreadyPaidPath,
  markAlreadyPaid,
} from "./sessionCompletion";

// Track Turbo restore visits (browser back/forward) so wallet-connecting
// controllers can detect stale cached pages and force a clean reload.
document.addEventListener("turbo:visit", (event) => {
  if (event.detail?.action === "restore") {
    sessionStorage.setItem(storage.TURBO_RESTORE_PENDING, "1");
  } else {
    sessionStorage.removeItem(storage.TURBO_RESTORE_PENDING);
    sessionStorage.removeItem(storage.TURBO_RESTORE_RELOADED);
  }

  // Use the visit destination — window.location may still be the previous step
  // when we mark completed and then Turbo.visit an outcome URL.
  let pathname = window.location.pathname;
  const visitURL = event.detail?.url;
  if (visitURL) {
    try {
      pathname = new URL(visitURL, window.location.origin).pathname;
    } catch (_) {
      // Keep current pathname if the visit URL is malformed.
    }
  }
  // BE middleware may land here without the FE having marked yet.
  if (isAlreadyPaidPath(pathname)) {
    markAlreadyPaid();
  }
  guardCompletedSession(pathname);
});

window.Stimulus = Application.start();

Stimulus.debug = process.env.PARCEL_DEBUG_MODE;

// Steps
Stimulus.register("authorisation-dfns-with-permit", AuthorisationDFNSWithPermitSupportController);
Stimulus.register("authorisation-dfns-without-permit", AuthorisationDFNSWithoutPermitSupportController);
Stimulus.register("authorisation-3rd-party-with-permit", Authorisation3rdPartyWithPermitSupportController);
Stimulus.register("authorisation-3rd-party-without-permit", Authorisation3rdPartyWithoutPermitSupportController);
Stimulus.register("blockchain-progress", BlockchainProgressController);
Stimulus.register("checkout-form", CheckoutFormController);
Stimulus.register("add-funds-wallet", AddFundsWalletController);
Stimulus.register("coinsub-payment-options", CoinsubPaymentOptionsController);
Stimulus.register("direct-deposit", DirectDepositController);
Stimulus.register("direct-deposit-result", DirectDepositResultController);
Stimulus.register("otp", OtpController);
Stimulus.register("passkey", PasskeyController);
Stimulus.register("payment-options", PaymentOptionsController);
Stimulus.register("connect-wallet-only", ConnectWalletOnlyController);
Stimulus.register("not-enough-balance", NotEnoughBalanceController);
Stimulus.register("third-party-payment-options", ThirdPartyPaymentOptionsController);
Stimulus.register("order-summary", OrderSummaryController);
Stimulus.register("review-dfns", ReviewDFNSController);
Stimulus.register("review-3rd-party-wallet", Review3rdPartyWalletController);
Stimulus.register("fast-track-payment", FastTrackPaymentController);
Stimulus.register("fast-track-otp-permit", FastTrackOtpPermitController);
Stimulus.register("fast-track-otp-approve", FastTrackOtpApproveController);
Stimulus.register("fast-track-otp-pay", FastTrackOtpPayController);

Stimulus.register("checkout", CheckoutController);
Stimulus.register("loadingscreen", LoadingScreenController);
Stimulus.register("copy", CopyController);
Stimulus.register("modal-container", ModalContainerController);
Stimulus.register("bottom-sheet", BottomSheetController);
Stimulus.register("error-surface", ErrorSurfaceController);
Stimulus.register("toast", ToastController);
Stimulus.register("socket", SocketController);
Stimulus.register("analytics", AnalyticsController);

Stimulus.handleError = (error, message, detail) => {
  debugError(message, detail, error);
};

document.addEventListener("turbo:before-frame-render", (event) => {
  if (event.target.id !== "step_content") return;
  if (event.detail?.action === "restore") return;

  event.preventDefault();

  const { resume } = event.detail;

  fadeOutAnimationOnRender().then(() => resume());
});

document.addEventListener("turbo:frame-render", (event) => {
  if (event.target.id !== "step_content") return;
  if (guardCompletedSession()) return;
  fadeInAnimationOnRender(event.target);
});

// Fast-track passkey steps (Authorize Allowance / Approve / Pay) arrive via
// Turbo.renderStreamMessage, which does not fire turbo:frame-render. Without
// this hook, .step-content / .step-button stay at the CSS opacity-0 baseline.
document.addEventListener("turbo:before-stream-render", (event) => {
  if (event.target.getAttribute("target") !== "step_content") return;
  if (guardCompletedSession()) {
    event.preventDefault();
    return;
  }

  const originalRender = event.detail.render;
  event.detail.render = async (streamElement) => {
    await fadeOutAnimationOnRender();
    await originalRender(streamElement);
    fadeInAnimationOnRender();
  };
});

function ensureStepVisibleOnLoad() {
  if (isAlreadyPaidPath()) {
    markAlreadyPaid();
  }
  if (guardCompletedSession()) return;

  const stepFrame = document.getElementById("step_content");
  if (!stepFrame) return;

  hideStaleLoadingScreen();
  fadeInAnimationOnRender();
}

// A history-restored snapshot can carry the loading overlay captured mid-transition,
// and the controllers' hide events fire before the loadingscreen controller listens.
function hideStaleLoadingScreen() {
  const overlay = document.querySelector('[data-controller~="loadingscreen"]');
  if (!overlay) return;

  overlay.style.opacity = 0;
  overlay.classList.add("hidden");
}

document.addEventListener("turbo:load", ensureStepVisibleOnLoad);
document.addEventListener("DOMContentLoaded", ensureStepVisibleOnLoad);

function fadeOutAnimationOnRender() {
  return new Promise((resolve) => {
    const stepContent = document.getElementsByClassName("step-content")[0];
    const stepButton = document.getElementsByClassName("step-button")[0];

    if (stepContent) {
      stepContent.style.opacity = 0;
      stepContent.style.transform = "translateY(-20px)";
    }

    if (stepButton) {
      stepButton.style.opacity = 0;
      stepButton.style.transform = "translateY(20px)";
    }

    setTimeout(() => {
      resolve();
    }, 300);
  });
}

function fadeInAnimationOnRender() {
  const stepContent = document.getElementsByClassName("step-content")[0];
  const stepButton = document.getElementsByClassName("step-button")[0];

  if (stepContent) {
    stepContent.style.opacity = 1;
    stepContent.style.transform = "translateY(0px)";
  }

  if (stepButton) {
    stepButton.style.opacity = 1;
    stepButton.style.transform = "translateY(0px)";
  }
}
